import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const GeneralSetup = () => {
  const [settings, setSettings] = useState({
    // Business Settings
    fiscalYearStart: '01-04',
    businessHours: {
      start: '09:00',
      end: '17:00',
      timezone: 'America/New_York'
    },
    workingDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    
    // Number Formats
    numberFormat: 'US',
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.',
    
    // Document Settings
    documentPrefix: 'DOC',
    documentNumbering: 'sequential',
    resetNumberingAnnually: true,
    
    // System Settings
    enableAuditLog: true,
    autoBackup: true,
    backupFrequency: 'daily',
    dataRetentionPeriod: 7,
    
    // Integration Settings
    enableAPIAccess: true,
    enableWebhooks: false,
    enableSSO: false,
    
    // Security Settings
    passwordPolicy: 'medium',
    sessionTimeout: 30,
    ipWhitelist: '',
    
    // Notification Settings
    systemNotifications: true,
    maintenanceNotifications: true,
    securityAlerts: true
  });

  const [originalSettings, setOriginalSettings] = useState({ ...settings });
  const [isModified, setIsModified] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [actionStatus, setActionStatus] = useState({ type: '', message: '' });

  const fiscalYearOptions = [
    { value: '01-01', label: 'January 1 - December 31' },
    { value: '01-04', label: 'April 1 - March 31' },
    { value: '01-07', label: 'July 1 - June 30' },
    { value: '01-10', label: 'October 1 - September 30' }
  ];

  const numberFormatOptions = [
    { value: 'US', label: 'US (1,234.56)', example: '1,234.56' },
    { value: 'EU', label: 'European (1.234,56)', example: '1.234,56' },
    { value: 'UK', label: 'UK (1,234.56)', example: '1,234.56' },
    { value: 'IN', label: 'Indian (1,23,456.78)', example: '1,23,456.78' }
  ];

  const documentNumberingOptions = [
    { value: 'sequential', label: 'Sequential (1, 2, 3...)' },
    { value: 'year-sequential', label: 'Year + Sequential (2025001, 2025002...)' },
    { value: 'month-sequential', label: 'Month + Sequential (Jan001, Jan002...)' },
    { value: 'custom', label: 'Custom Format' }
  ];

  const passwordPolicyOptions = [
    { value: 'low', label: 'Low - Minimum 6 characters' },
    { value: 'medium', label: 'Medium - 8 characters, mixed case' },
    { value: 'high', label: 'High - 12 characters, complex' }
  ];

  const backupFrequencyOptions = [
    { value: 'hourly', label: 'Hourly' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' }
  ];

  const workingDayOptions = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' }
  ];

  const timezoneOptions = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'Europe/London', label: 'Greenwich Mean Time (GMT)' },
    { value: 'Europe/Paris', label: 'Central European Time (CET)' },
    { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)' },
    { value: 'Australia/Sydney', label: 'Australian Eastern Time (AET)' }
  ];

  const validateSettings = () => {
    const errors = [];
    
    if (settings?.sessionTimeout < 5 || settings?.sessionTimeout > 480) {
      errors?.push('Session timeout must be between 5 and 480 minutes');
    }
    
    if (settings?.dataRetentionPeriod < 1 || settings?.dataRetentionPeriod > 50) {
      errors?.push('Data retention period must be between 1 and 50 years');
    }
    
    if (settings?.decimalPlaces < 0 || settings?.decimalPlaces > 6) {
      errors?.push('Decimal places must be between 0 and 6');
    }
    
    if (settings?.ipWhitelist && settings?.ipWhitelist?.trim()) {
      const ipPattern = /^(\d{1,3}\.){3}\d{1,3}$/;
      const ips = settings?.ipWhitelist?.split(',')?.map(ip => ip?.trim());
      const invalidIps = ips?.filter(ip => ip && !ipPattern?.test(ip));
      if (invalidIps?.length > 0) {
        errors?.push('Please enter valid IP addresses in the whitelist');
      }
    }

    return errors;
  };

  const handleSettingChange = (key, value) => {
    if (key?.includes('.')) {
      const keys = key?.split('.');
      setSettings(prev => ({
        ...prev,
        [keys?.[0]]: {
          ...prev?.[keys?.[0]],
          [keys?.[1]]: value
        }
      }));
    } else {
      setSettings(prev => ({
        ...prev,
        [key]: value
      }));
    }
    
    setIsModified(true);
    // Clear status messages when user makes changes
    if (actionStatus?.message) {
      setActionStatus({ type: '', message: '' });
    }
  };

  const handleWorkingDayToggle = (day) => {
    const newWorkingDays = settings?.workingDays?.includes(day)
      ? settings?.workingDays?.filter(d => d !== day)
      : [...settings?.workingDays, day];
    
    if (newWorkingDays?.length === 0) {
      setActionStatus({ 
        type: 'error', 
        message: 'At least one working day must be selected' 
      });
      return;
    }
    
    handleSettingChange('workingDays', newWorkingDays);
  };

  const handleSave = async () => {
    const validationErrors = validateSettings();
    
    if (validationErrors?.length > 0) {
      setActionStatus({ 
        type: 'error', 
        message: validationErrors?.join(', ') 
      });
      return;
    }

    setIsSaving(true);
    setActionStatus({ type: '', message: '' });

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      console.log('Saving general settings:', settings);
      
      setOriginalSettings({ ...settings });
      setIsModified(false);
      
      setActionStatus({ 
        type: 'success', 
        message: 'Settings saved successfully!' 
      });

      setTimeout(() => {
        setActionStatus({ type: '', message: '' });
      }, 3000);

    } catch (error) {
      setActionStatus({ 
        type: 'error', 
        message: 'Failed to save settings. Please try again.' 
      });
    } finally {
      setIsSaving(false);
    }
  };

  const resetToDefaults = () => {
    const confirmed = window.confirm(
      'This will reset all settings to their default values. Are you sure you want to continue?'
    );
    if (!confirmed) return;

    const defaultSettings = {
      fiscalYearStart: '01-04',
      businessHours: {
        start: '09:00',
        end: '17:00',
        timezone: 'America/New_York'
      },
      workingDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      numberFormat: 'US',
      decimalPlaces: 2,
      thousandSeparator: ',',
      decimalSeparator: '.',
      documentPrefix: 'DOC',
      documentNumbering: 'sequential',
      resetNumberingAnnually: true,
      enableAuditLog: true,
      autoBackup: true,
      backupFrequency: 'daily',
      dataRetentionPeriod: 7,
      enableAPIAccess: true,
      enableWebhooks: false,
      enableSSO: false,
      passwordPolicy: 'medium',
      sessionTimeout: 30,
      ipWhitelist: '',
      systemNotifications: true,
      maintenanceNotifications: true,
      securityAlerts: true
    };

    setSettings(defaultSettings);
    setIsModified(JSON.stringify(defaultSettings) !== JSON.stringify(originalSettings));
    
    setActionStatus({ 
      type: 'success', 
      message: 'Settings reset to defaults' 
    });

    setTimeout(() => {
      setActionStatus({ type: '', message: '' });
    }, 3000);
  };

  const handleCancel = () => {
    if (!isModified) return;
    
    const confirmed = window.confirm(
      'You have unsaved changes. Are you sure you want to discard them?'
    );
    if (!confirmed) return;
    
    setSettings({ ...originalSettings });
    setIsModified(false);
    setActionStatus({ type: '', message: '' });
  };

  const testConfiguration = async () => {
    setActionStatus({ type: '', message: '' });
    
    try {
      // Simulate configuration test
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setActionStatus({ 
        type: 'success', 
        message: 'Configuration test completed successfully! All systems are working correctly.' 
      });

      setTimeout(() => {
        setActionStatus({ type: '', message: '' });
      }, 5000);

    } catch (error) {
      setActionStatus({ 
        type: 'error', 
        message: 'Configuration test failed. Please check your settings.' 
      });
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">General Setup</h2>
          <p className="text-muted-foreground">Configure core system settings and business parameters</p>
        </div>
        <div className="flex items-center space-x-3">
          {isModified && (
            <span className="text-sm text-yellow-600 dark:text-yellow-400">
              Unsaved changes
            </span>
          )}
          <Button 
            variant="outline" 
            onClick={resetToDefaults}
            disabled={isSaving}
          >
            Reset to Defaults
          </Button>
          <Button 
            onClick={handleSave} 
            iconName="Save"
            disabled={!isModified || isSaving}
            className={isSaving ? 'opacity-50' : ''}
          >
            {isSaving ? (
              <>
                <Icon name="Loader2" size={16} className="animate-spin mr-2" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </div>
      {/* Status Messages */}
      {actionStatus?.message && (
        <div className={`p-4 rounded-lg border ${
          actionStatus?.type === 'success' ?'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400' :'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400'
        }`}>
          <div className="flex items-center space-x-2">
            <Icon 
              name={actionStatus?.type === 'success' ? 'CheckCircle' : 'AlertCircle'} 
              size={16} 
            />
            <span className="text-sm font-medium">{actionStatus?.message}</span>
          </div>
        </div>
      )}
      {/* Business Settings */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-6">Business Settings</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Fiscal Year Start
            </label>
            <select
              value={settings?.fiscalYearStart}
              onChange={(e) => handleSettingChange('fiscalYearStart', e?.target?.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {fiscalYearOptions?.map(option => (
                <option key={option?.value} value={option?.value}>{option?.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Business Timezone
            </label>
            <select
              value={settings?.businessHours?.timezone}
              onChange={(e) => handleSettingChange('businessHours.timezone', e?.target?.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {timezoneOptions?.map(tz => (
                <option key={tz?.value} value={tz?.value}>{tz?.label}</option>
              ))}
            </select>
          </div>

          <div className="space-y-4">
            <h4 className="text-sm font-medium text-foreground">Business Hours</h4>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Start Time"
                type="time"
                value={settings?.businessHours?.start}
                onChange={(e) => handleSettingChange('businessHours.start', e?.target?.value)}
              />
              <Input
                label="End Time"
                type="time"
                value={settings?.businessHours?.end}
                onChange={(e) => handleSettingChange('businessHours.end', e?.target?.value)}
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-3 block">
              Working Days
            </label>
            <div className="grid grid-cols-7 gap-2">
              {workingDayOptions?.map(day => (
                <button
                  key={day?.value}
                  onClick={() => handleWorkingDayToggle(day?.value)}
                  className={`p-2 text-sm rounded-md border transition-colors ${
                    settings?.workingDays?.includes(day?.value)
                      ? 'border-primary bg-primary/10 text-primary' :'border-border hover:border-primary/50 text-foreground'
                  }`}
                >
                  {day?.label?.slice(0, 3)}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Select the days your business operates
            </p>
          </div>
        </div>
      </div>
      {/* Number Formats */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-6">Number Formats</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Number Format
            </label>
            <select
              value={settings?.numberFormat}
              onChange={(e) => handleSettingChange('numberFormat', e?.target?.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {numberFormatOptions?.map(option => (
                <option key={option?.value} value={option?.value}>{option?.label}</option>
              ))}
            </select>
          </div>

          <Input
            label="Decimal Places"
            type="number"
            value={settings?.decimalPlaces}
            onChange={(e) => handleSettingChange('decimalPlaces', parseInt(e?.target?.value))}
            min="0"
            max="6"
          />

          <Input
            label="Thousand Separator"
            value={settings?.thousandSeparator}
            onChange={(e) => handleSettingChange('thousandSeparator', e?.target?.value)}
            placeholder=","
            maxLength="1"
          />

          <Input
            label="Decimal Separator"
            value={settings?.decimalSeparator}
            onChange={(e) => handleSettingChange('decimalSeparator', e?.target?.value)}
            placeholder="."
            maxLength="1"
          />
        </div>

        <div className="mt-4 p-3 bg-muted/30 rounded-lg">
          <p className="text-sm text-foreground">
            <span className="font-medium">Preview: </span>
            {numberFormatOptions?.find(f => f?.value === settings?.numberFormat)?.example || '1,234.56'}
          </p>
        </div>
      </div>
      {/* Document Settings */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-6">Document Settings</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Document Prefix"
            value={settings?.documentPrefix}
            onChange={(e) => handleSettingChange('documentPrefix', e?.target?.value)}
            placeholder="DOC"
            description="Prefix for all document numbers"
          />

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Document Numbering
            </label>
            <select
              value={settings?.documentNumbering}
              onChange={(e) => handleSettingChange('documentNumbering', e?.target?.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {documentNumberingOptions?.map(option => (
                <option key={option?.value} value={option?.value}>{option?.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={settings?.resetNumberingAnnually}
              onChange={(e) => handleSettingChange('resetNumberingAnnually', e?.target?.checked)}
              className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
            />
            <span className="text-sm text-foreground">Reset document numbering annually</span>
          </label>
        </div>
      </div>
      {/* System Settings */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-6">System Settings</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-sm font-medium text-foreground">Enable Audit Log</span>
                <p className="text-xs text-muted-foreground">Track all system activities</p>
              </div>
              <input
                type="checkbox"
                checked={settings?.enableAuditLog}
                onChange={(e) => handleSettingChange('enableAuditLog', e?.target?.checked)}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </label>

            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-sm font-medium text-foreground">Auto Backup</span>
                <p className="text-xs text-muted-foreground">Automatic data backups</p>
              </div>
              <input
                type="checkbox"
                checked={settings?.autoBackup}
                onChange={(e) => handleSettingChange('autoBackup', e?.target?.checked)}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </label>

            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-sm font-medium text-foreground">Enable API Access</span>
                <p className="text-xs text-muted-foreground">Allow external API connections</p>
              </div>
              <input
                type="checkbox"
                checked={settings?.enableAPIAccess}
                onChange={(e) => handleSettingChange('enableAPIAccess', e?.target?.checked)}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </label>

            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-sm font-medium text-foreground">Enable Webhooks</span>
                <p className="text-xs text-muted-foreground">Real-time event notifications</p>
              </div>
              <input
                type="checkbox"
                checked={settings?.enableWebhooks}
                onChange={(e) => handleSettingChange('enableWebhooks', e?.target?.checked)}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </label>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Backup Frequency
              </label>
              <select
                value={settings?.backupFrequency}
                onChange={(e) => handleSettingChange('backupFrequency', e?.target?.value)}
                disabled={!settings?.autoBackup}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
              >
                {backupFrequencyOptions?.map(option => (
                  <option key={option?.value} value={option?.value}>{option?.label}</option>
                ))}
              </select>
            </div>

            <Input
              label="Data Retention (years)"
              type="number"
              value={settings?.dataRetentionPeriod}
              onChange={(e) => handleSettingChange('dataRetentionPeriod', parseInt(e?.target?.value))}
              min="1"
              max="50"
              description="How long to keep system data"
            />

            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Password Policy
              </label>
              <select
                value={settings?.passwordPolicy}
                onChange={(e) => handleSettingChange('passwordPolicy', e?.target?.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                {passwordPolicyOptions?.map(option => (
                  <option key={option?.value} value={option?.value}>{option?.label}</option>
                ))}
              </select>
            </div>

            <Input
              label="Session Timeout (minutes)"
              type="number"
              value={settings?.sessionTimeout}
              onChange={(e) => handleSettingChange('sessionTimeout', parseInt(e?.target?.value))}
              min="5"
              max="480"
              description="Auto-logout after inactivity"
            />
          </div>
        </div>
      </div>
      {/* Security & Notifications */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-6">Security & Notifications</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Input
              label="IP Whitelist"
              value={settings?.ipWhitelist}
              onChange={(e) => handleSettingChange('ipWhitelist', e?.target?.value)}
              placeholder="192.168.1.1, 10.0.0.1"
              description="Comma-separated list of allowed IP addresses"
            />
          </div>

          <div className="space-y-4">
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-sm font-medium text-foreground">System Notifications</span>
              <input
                type="checkbox"
                checked={settings?.systemNotifications}
                onChange={(e) => handleSettingChange('systemNotifications', e?.target?.checked)}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </label>

            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-sm font-medium text-foreground">Maintenance Notifications</span>
              <input
                type="checkbox"
                checked={settings?.maintenanceNotifications}
                onChange={(e) => handleSettingChange('maintenanceNotifications', e?.target?.checked)}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </label>

            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-sm font-medium text-foreground">Security Alerts</span>
              <input
                type="checkbox"
                checked={settings?.securityAlerts}
                onChange={(e) => handleSettingChange('securityAlerts', e?.target?.checked)}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </label>
          </div>
        </div>
      </div>
      {/* Quick Actions */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-4">System Maintenance</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Button 
            variant="outline" 
            iconName="TestTube" 
            className="justify-start"
            onClick={testConfiguration}
          >
            Test Configuration
          </Button>
          <Button 
            variant="outline" 
            iconName="Download" 
            className="justify-start"
          >
            Export Settings
          </Button>
          <Button 
            variant="outline" 
            iconName="Upload" 
            className="justify-start"
          >
            Import Settings
          </Button>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="flex justify-end space-x-4 pt-6 border-t border-border">
        <Button 
          variant="outline" 
          onClick={handleCancel}
          disabled={isSaving || !isModified}
        >
          Cancel
        </Button>
        <Button 
          variant="outline" 
          onClick={resetToDefaults}
          disabled={isSaving}
        >
          Reset to Defaults
        </Button>
        <Button 
          onClick={handleSave} 
          iconName="Save"
          disabled={!isModified || isSaving}
          className={isSaving ? 'opacity-50' : ''}
        >
          {isSaving ? (
            <>
              <Icon name="Loader2" size={16} className="animate-spin mr-2" />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </div>
    </div>
  );
};

export default GeneralSetup;